// OMON: option monitor on CBOE's 15-minute delayed chains.

import { createOptionsChart, LineSeries, LineStyle, type DeepPartial, type PriceChartOptions } from "../../vendor/lwc.mjs";
import { get, type Meta } from "../api";
import { COLORS, THEME } from "../chart";
import { button, clear, errorBox, h, loading, type Child } from "../dom";
import * as f from "../fmt";
import { register, type Ctx } from "../registry";

interface Overview {
  symbol: string;
  root: string;
  time: number | null;
  price: number | null;
  prevClose: number | null;
  iv30: number | null;
  expiries: ExpiryInfo[];
}

interface ExpiryInfo {
  date: string;
  root: string;
  strikes: number;
  callVol: number;
  putVol: number;
  callOi: number;
  putOi: number;
}

interface Contract {
  bid?: number;
  ask?: number;
  last?: number;
  chg?: number;
  vol?: number;
  oi?: number;
  iv?: number;
  delta?: number;
  gamma?: number;
  theta?: number;
  vega?: number;
}

interface Strike {
  strike: number;
  call?: Contract;
  put?: Contract;
}

interface Chain {
  date: string;
  root: string;
  /// Spot and time of this chain's snapshot.
  price: number | null;
  time: number | null;
  rows: Strike[];
}

const PREFS = "berg.omon";
interface Prefs {
  /// Strikes either side of spot; 0 for all.
  window: number;
  greeks: boolean;
  smile: boolean;
}
function loadPrefs(): Prefs {
  const def: Prefs = { window: 10, greeks: false, smile: false };
  try {
    return { ...def, ...JSON.parse(localStorage.getItem(PREFS) ?? "{}") };
  } catch {
    return def;
  }
}

const WINDOWS: [number, string][] = [
  [5, "±5"],
  [10, "±10"],
  [20, "±20"],
  [0, "ALL"],
];

/// Index options settled on the morning's opening prints: they stop trading
/// the day before their expiration date.
const AM_SETTLED = new Set(["SPX", "NDX", "RUT", "VIX", "DJX"]);

/// Today in New York and whether the 16:15 close has passed: CBOE leaves
/// the day's expired series in the file until the next session.
function nyNow(): { date: string; closed: boolean } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date());
  const p = Object.fromEntries(parts.map((x) => [x.type, x.value]));
  return { date: `${p.year}-${p.month}-${p.day}`, closed: Number(p.hour) * 60 + Number(p.minute) >= 16 * 60 + 15 };
}

function dte(date: string, today: string): number {
  return Math.round((Date.parse(date) - Date.parse(today)) / 86_400_000);
}

/// Index of the strike nearest `spot`, or -1.
function atmIndex(rows: Strike[], spot: number | undefined): number {
  if (spot === undefined) return -1;
  let best = -1;
  rows.forEach((r, i) => {
    if (best < 0 || Math.abs(r.strike - spot) < Math.abs(rows[best].strike - spot)) best = i;
  });
  return best;
}

/// Quote midpoint (a zero bid is a real bid), else the last trade.
const mid = (c?: Contract) => (c?.ask !== undefined && c.bid !== undefined ? (c.bid + c.ask) / 2 : c?.last);
const ratio = (a: number, b: number) => (b > 0 ? f.fixedN(a / b) : f.NA);
const ivPct = (v: unknown) => (f.num(v) === undefined ? f.NA : f.fixedN((v as number) * 100, 1));

interface Col {
  label: string;
  title?: string;
  cell(c: Contract): Child;
  cls?: (c: Contract) => string;
}

const px = (v: unknown) => f.fixedN(v, 2);
const BASE_COLS: Col[] = [
  { label: "Bid", cell: (c) => px(c.bid) },
  { label: "Ask", cell: (c) => px(c.ask) },
  { label: "Last", cell: (c) => px(c.last) },
  { label: "Chg", cell: (c) => (c.chg ? f.chg(c.chg, 2) : f.NA), cls: (c) => f.cls(c.chg) },
  { label: "Volm", title: "Volume today", cell: (c) => (c.vol ? f.big(c.vol, 1) : f.NA) },
  { label: "OI", title: "Open interest", cell: (c) => (c.oi ? f.big(c.oi, 1) : f.NA) },
  { label: "IV%", title: "Implied volatility", cell: (c) => ivPct(c.iv) },
  { label: "Δ", title: "Delta", cell: (c) => f.fixedN(c.delta, 2) },
];
const GREEK_COLS: Col[] = [
  { label: "Γ", title: "Gamma", cell: (c) => f.fixedN(c.gamma, 4) },
  { label: "Θ", title: "Theta, per day", cell: (c) => f.fixedN(c.theta, 3) },
  { label: "V", title: "Vega, per vol point", cell: (c) => f.fixedN(c.vega, 3) },
];

function run(ctx: Ctx): void {
  const sym = ctx.sec!;
  const prefs = loadPrefs();
  const save = () => localStorage.setItem(PREFS, JSON.stringify(prefs));

  // Toolbar and summary stay hidden until there is a chain to describe.
  const toolbar = h("div", { class: "toolbar", style: { display: "none" } });
  const summary = h("div", { class: "summary", style: { display: "none" } });
  const chartWrap = h("div", { class: "chartwrap", style: { flex: "none", height: "170px", borderBottom: "1px solid var(--line)", display: "none" } });
  const scroll = h("div", { class: "scroll" }, loading());
  clear(ctx.body, h("div", { class: "fill" }, toolbar, summary, chartWrap, scroll));

  let ov: Overview | null = null;
  let chain: Chain | null = null;
  let chainMeta: Meta | null = null;
  let sel = -1;
  /// Bumped per chain request; older answers and errors are dropped.
  let chainSeq = 0;
  let chartAbort: AbortController | null = null;
  ctx.signal.addEventListener("abort", () => chartAbort?.abort());
  let scrolled = false;

  const spot = () => chain?.price ?? ov?.price ?? undefined;

  /// Default expiry: the first that hasn't finished trading.
  const pickDefault = (o: Overview) => {
    const now = nyNow();
    const live = (e: ExpiryInfo) =>
      e.date > now.date || (e.date === now.date && !now.closed && !AM_SETTLED.has(e.root));
    const i = o.expiries.findIndex(live);
    return i < 0 ? 0 : i;
  };

  const drawToolbar = () => {
    if (!ov) return clear(toolbar);
    toolbar.style.display = "";
    const today = nyNow().date;
    const select = h("select", {
      title: "Expiry",
      onchange: () => {
        sel = Number(select.value);
        scrolled = false;
        loadChain();
      },
    });
    const multiRoot = new Set(ov.expiries.map((e) => e.root)).size > 1;
    ov.expiries.forEach((e, i) => {
      const label = `${e.date}${multiRoot ? ` ${e.root}` : ""}  ${dte(e.date, today)}d  ${e.strikes} strikes`;
      select.append(h("option", { value: String(i), selected: i === sel ? "" : undefined }, label));
    });
    const step = (d: number) => () => {
      const n = sel + d;
      if (!ov || n < 0 || n >= ov.expiries.length) return;
      sel = n;
      scrolled = false;
      drawToolbar();
      loadChain();
    };
    const pref = (change: () => void) => () => {
      change();
      save();
      drawToolbar();
      render();
    };
    clear(
      toolbar,
      h("label", null, "Expiry"),
      button("◀", step(-1), false, "Previous expiry"),
      select,
      button("▶", step(1), false, "Next expiry"),
      h("span", { class: "sep" }),
      h("label", null, "Strikes"),
      ...WINDOWS.map(([n, label]) =>
        button(
          label,
          pref(() => {
            prefs.window = n;
            scrolled = false;
          }),
          prefs.window === n,
        ),
      ),
      h("span", { class: "sep" }),
      button("Greeks", pref(() => (prefs.greeks = !prefs.greeks)), prefs.greeks, "Gamma, theta, vega"),
      button("Smile", pref(() => (prefs.smile = !prefs.smile)), prefs.smile, "IV by strike"),
    );
  };

  /// Rows within the strike window around spot.
  const visibleRows = (c: Chain): Strike[] => {
    const atm = atmIndex(c.rows, spot());
    if (!prefs.window || atm < 0) return c.rows;
    return c.rows.slice(Math.max(0, atm - prefs.window), atm + prefs.window + 1);
  };

  const drawSummary = (c: Chain) => {
    const e = ov?.expiries.find((x) => x.date === c.date && x.root === c.root);
    if (!ov || !e) return;
    const s = spot();
    const atm: Strike | undefined = c.rows[atmIndex(c.rows, s)];
    const ivs = [atm?.call?.iv, atm?.put?.iv].filter((v): v is number => v !== undefined);
    const atmIv = ivs.length ? ivs.reduce((a, b) => a + b, 0) / ivs.length : undefined;
    const cm = mid(atm?.call);
    const pm = mid(atm?.put);
    const straddle = cm !== undefined && pm !== undefined ? cm + pm : undefined;
    const tot = ov.expiries.reduce((a, x) => ({ cv: a.cv + x.callVol, pv: a.pv + x.putVol, co: a.co + x.callOi, po: a.po + x.putOi }), { cv: 0, pv: 0, co: 0, po: 0 });
    const item = (k: string, v: Child, title?: string) => h("span", { title }, h("span", { class: "k" }, k), v);
    summary.style.display = "";
    clear(
      summary,
      item("Spot", h("b", null, f.price(s)), "Underlying in CBOE's snapshot"),
      ov.iv30 ? item("IV30", f.fixedN(ov.iv30, 1), "CBOE 30-day implied volatility index") : null,
      item("ATM", atm ? `${f.fixedN(atm.strike, 2)} · IV ${ivPct(atmIv)}` : f.NA, "Strike nearest spot, mean of call and put IV"),
      item(
        "Exp move",
        straddle !== undefined && s ? `±${f.fixedN(straddle, 2)} (±${f.fixedN((straddle / s) * 100, 1)}%)` : f.NA,
        "ATM straddle (quote midpoints, else last trades): the market's priced move to expiry",
      ),
      item("P/C vol", `${ratio(e.putVol, e.callVol)} · all ${ratio(tot.pv, tot.cv)}`, "Put/call volume: this expiry · all expiries"),
      item("P/C OI", `${ratio(e.putOi, e.callOi)} · all ${ratio(tot.po, tot.co)}`, "Put/call open interest: this expiry · all expiries"),
      ov.root === "_VIX" ? h("span", { class: "dim" }, "VIX options price off VIX futures, not the spot index") : null,
    );
  };

  const drawSmile = (rows: Strike[]) => {
    chartAbort?.abort();
    chartWrap.style.display = prefs.smile ? "" : "none";
    if (!prefs.smile) return;
    chartAbort = new AbortController();
    const abort = chartAbort.signal;
    const box = h("div", { class: "chartbox" });
    const s = spot() ?? 0;
    const legend = h(
      "div",
      { class: "legend" },
      h("span", { class: "amber" }, "OTM IV%"),
      h("span", { style: { color: "#6fb6ff" } }, "Call IV%"),
      h("span", { style: { color: "#ff6fd8" } }, "Put IV%"),
    );
    clear(chartWrap, box, legend);
    const precision = rows.some((r) => r.strike % 1) ? 2 : 0;
    const opts = { ...THEME, localization: { precision }, timeScale: { borderColor: "#2e2e2e" } } as DeepPartial<PriceChartOptions>;
    const chart = createOptionsChart(box, opts);
    abort.addEventListener("abort", () => chart.remove());
    // IV means little deep in or far out of the money (|delta| outside
    // 2-98%), where quotes are a tick wide and the solver runs wild.
    const iv = (c?: Contract) => {
      const d = Math.abs(c?.delta ?? 0);
      return c?.iv && d >= 0.02 && d <= 0.98 ? c.iv * 100 : undefined;
    };
    const series = (pick: (r: Strike) => Contract | undefined) =>
      rows.flatMap((r) => {
        const v = iv(pick(r));
        return v === undefined ? [] : [{ time: r.strike, value: v }];
      });
    const faint = { lineWidth: 1, lineStyle: LineStyle.Dotted, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false } as const;
    chart.addSeries(LineSeries, { ...faint, color: "#6fb6ff" }).setData(series((r) => r.call));
    chart.addSeries(LineSeries, { ...faint, color: "#ff6fd8" }).setData(series((r) => r.put));
    // The smile proper: out-of-the-money puts below spot, calls above.
    const otm = series((r) => (r.strike >= s ? r.call : r.put));
    chart.addSeries(LineSeries, { color: COLORS.amber, lineWidth: 2, priceLineVisible: false }).setData(otm);
    chart.timeScale().fitContent();
  };

  const render = () => {
    if (!ov || !chain) return;
    const c = chain;
    const s = spot();
    const rows = visibleRows(c);
    drawSummary(c);
    drawSmile(rows);

    const cols = prefs.greeks ? [...BASE_COLS, ...GREEK_COLS] : BASE_COLS;
    const callCols = [...cols].reverse();
    const n = cols.length;
    const side = (label: string) => h("th", { class: "side", colspan: String(n) }, label);
    const head1 = h("tr", null, side("CALLS"), h("th", { class: "k" }, ""), side("PUTS"));
    const head2 = h(
      "tr",
      null,
      ...callCols.map((col) => h("th", { class: "num", title: col.title }, col.label)),
      h("th", { class: "k" }, "Strike"),
      ...cols.map((col) => h("th", { class: "num", title: col.title }, col.label)),
    );
    const cells = (con: Contract | undefined, list: Col[], itm: boolean) =>
      list.map((col) => h("td", { class: `num${itm ? " itm" : ""} ${con && col.cls ? col.cls(con) : ""}` }, con ? col.cell(con) : ""));
    const body = h("tbody");
    let spotAt = -1;
    rows.forEach((r, i) => {
      const next = rows[i + 1];
      // Amber rule under the last strike at or below spot.
      const atSpot = s !== undefined && r.strike <= s && (!next || next.strike > s);
      if (atSpot) spotAt = i;
      body.append(
        h(
          "tr",
          { class: atSpot ? "spot" : undefined },
          ...cells(r.call, callCols, s !== undefined && r.strike < s),
          h("td", { class: "k" }, f.fixedN(r.strike, r.strike % 1 ? 2 : 0)),
          ...cells(r.put, cols, s !== undefined && r.strike > s),
        ),
      );
    });
    const thead = h("thead", null, head1, head2);
    clear(scroll, h("table", { class: "dt chain" }, thead, body));
    // Spot mid-screen on first show, a whole row under the header.
    if (!scrolled && spotAt >= 0) {
      scrolled = true;
      const headH = thead.getBoundingClientRect().height;
      const rowH = (body.children[0] as HTMLElement).offsetHeight || 1;
      const fit = Math.floor((scroll.clientHeight - headH) / rowH);
      const first = Math.max(0, spotAt - Math.floor(fit / 2));
      scroll.scrollTop = (body.children[first] as HTMLElement).offsetTop - headH;
    }
    ctx.panel.status(
      `CBOE · 15-min delayed · ${c.root} ${c.date} · ${rows.length}/${c.rows.length} strikes · snapshot ${c.time ? f.stamp(c.time) : f.NA}`,
      chainMeta ?? undefined,
    );
  };

  const loadChain = async () => {
    if (!ov || sel < 0) return;
    const e = ov.expiries[sel];
    const mine = ++chainSeq;
    try {
      const r = await get<Chain>("options/chain", { s: sym, exp: e.date, root: e.root }, ctx.signal);
      if (mine !== chainSeq) return;
      chain = r.data;
      chainMeta = r.meta;
      render();
    } catch (err) {
      if (mine === chainSeq && !ctx.signal.aborted) clear(scroll, errorBox(err));
    }
  };

  const load = async () => {
    const r = await get<Overview>("options", { s: sym }, ctx.signal);
    if (!r.data.expiries.length) throw new Error(`No listed options for ${sym}`);
    const prev = ov && sel >= 0 ? ov.expiries[sel] : null;
    ov = r.data;
    const keep = prev ? ov.expiries.findIndex((e) => e.date === prev.date && e.root === prev.root) : -1;
    sel = keep >= 0 ? keep : pickDefault(ov);
    drawToolbar();
    await loadChain();
  };

  load().catch((e) => {
    // Until a first success the error is all there is; after that, refresh
    // failures keep the last chain on screen.
    if (!ctx.signal.aborted && !ov) clear(scroll, errorBox(e));
  });
  // CBOE's file is cached for five minutes server-side.
  ctx.panel.every(300_000, load);
}

register({
  code: "OMON",
  name: "Option Monitor",
  group: "Equity",
  needsSec: true,
  help: "Option chain by expiry: bid/ask, volume, open interest, IV and greeks, IV smile. CBOE, 15-min delayed.",
  run,
});
